class CollectionWithSlider extends HTMLElement {
  constructor() {
    super();

    this.slidesCount = +this.dataset.slides;
    this.slidesToShow = +this.dataset.slidesToShow;
    this.slidesToShowMobile = +this.dataset.slidesToShowMobile;
    this.$swiper = null;
    this._tabGroupEl = null;
    this._onTabShow = this._updateSwiper.bind(this);
  }

  get name() {
    return this.getAttribute("name");
  }

  connectedCallback() {
    this._handleTabchange();
    this._init();
  }

  disconnectedCallback() {
    this._tabGroupEl?.removeEventListener("sl-tab-show", this._onTabShow);
    this._tabGroupEl = null;

    if (this.$swiper && typeof this.$swiper.destroy === "function") {
      this.$swiper.destroy(true, true);
    }
    this.$swiper = null;
  }

  _init() {
    if (this.$swiper) return;

    const swiperEl = this.querySelector(".js-collection-with-slider");
    const prevEl = this.querySelector(".featured-collection--icon-previous");
    const nextEl = this.querySelector(".featured-collection--icon-next");

    if (!swiperEl) return;

    // Swiper CDN may still be loading when this element connects.
    if (typeof Swiper === "undefined") {
      if (!this._swiperRetryStarted) {
        this._swiperRetryStarted = true;
        const startedAt = performance.now();
        const poll = () => {
          if (typeof Swiper !== "undefined") {
            this._init();
            return;
          }
          if (performance.now() - startedAt < 3000) {
            requestAnimationFrame(poll);
          }
        };
        requestAnimationFrame(poll);
      }
      return;
    }

    const option = {
      init: false,
      spaceBetween: 0,
      slidesPerView: this.slidesToShowMobile,
      preventClicks: false,
      preventClicksPropagation: false,
      slidesOffsetAfter: 0,
      slidesOffsetBefore: 0,
      centeredSlides: true,
      loop: true,
      loopAdditionalSlides: 2,
      navigation: { nextEl, prevEl },
      longSwipesRatio: 0.25,
      breakpoints: {
        640: { slidesPerView: 2 },
        990: { slidesPerView: 3 },
        1200: { slidesPerView: 4 },
        1400: { slidesPerView: this.slidesToShow },
        1800: { slidesPerView: this.slidesToShow },
      },
    };

    this._doubleSlidesIfNeeded(swiperEl);

    this.$swiper = new Swiper(swiperEl, option);
    this.$swiper.on("afterInit", () => this._doInit());
    this.$swiper.on("resize", () => this._doInit());
    this.$swiper.init();
  }

  _doInit() {
    const imgWrapper = this.$swiper?.el?.querySelector(".card__media");
    if (imgWrapper) {
      const h = imgWrapper.getBoundingClientRect().height;
      this.style.setProperty("--media-height", `${h}px`);
    }
  }

  _doubleSlidesIfNeeded(swiperEl) {
    const wrapper = swiperEl?.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    for (const n of wrapper.querySelectorAll(".js-dup-slide")) {
      n.remove();
    }

    const originals = Array.from(wrapper.children).filter(
      (n) => !n.classList.contains("js-dup-slide"),
    );

    if (originals.length > 0 && originals.length < 9) {
      for (const node of originals) {
        const clone = node.cloneNode(true);
        clone.classList.add("js-dup-slide");
        clone.setAttribute("aria-hidden", "true");
        wrapper.appendChild(clone);
      }
    }
  }

  _handleTabchange() {
    const tabGroupEl = this.closest("sl-tab-group");
    if (!tabGroupEl) return;

    this._tabGroupEl = tabGroupEl;
    tabGroupEl.addEventListener("sl-tab-show", this._onTabShow);

    // Lock height before Shoelace switches panels (sl-tab-show is too late).
    if (!tabGroupEl.__collectionSliderHeightBound) {
      tabGroupEl.__collectionSliderHeightBound = true;

      const lockBeforeSwitch = (event) => {
        const tab = CollectionWithSlider._tabFromEvent(event);
        if (!tab || !tabGroupEl.contains(tab)) return;

        const activation =
          tabGroupEl.activation ||
          tabGroupEl.getAttribute("activation") ||
          "auto";
        const arrowsSwitchTabs = activation !== "manual";
        const placement =
          tabGroupEl.placement ||
          tabGroupEl.getAttribute("placement") ||
          "top";
        const isHorizontal = placement === "top" || placement === "bottom";
        // Only orientation-matching arrows move selection in Shoelace.
        const arrowKeys = isHorizontal
          ? ["ArrowLeft", "ArrowRight"]
          : ["ArrowUp", "ArrowDown"];
        const endpointKeys = ["Home", "End"];
        const isArrowNav =
          event.type === "keydown" && arrowKeys.includes(event.key);
        const isEndpointNav =
          event.type === "keydown" && endpointKeys.includes(event.key);
        const isKeyboardNav = isArrowNav || isEndpointNav;

        if (event.type === "keydown") {
          const isActivateKey = event.key === "Enter" || event.key === " ";
          if (!isActivateKey && !(isKeyboardNav && arrowsSwitchTabs)) return;
        }

        // Enter/Space/click on the selected tab does not switch. Arrow/Home/End
        // on the selected tab may switch, so still lock height for those.
        if (!isKeyboardNav && tab.getAttribute("aria-selected") === "true") {
          return;
        }

        const activeBefore = CollectionWithSlider._activePanelName(tabGroupEl);
        CollectionWithSlider._lockTabBodyHeight(tabGroupEl);

        // Home/End (and arrows at the ends) can be no-ops. If the panel never
        // changes, sl-tab-show won't fire — unlock so min-height does not stick.
        if (event.type === "keydown") {
          requestAnimationFrame(() => {
            if (
              CollectionWithSlider._activePanelName(tabGroupEl) === activeBefore
            ) {
              CollectionWithSlider._unlockTabBodyHeight(tabGroupEl);
            }
          });
        }
      };

      tabGroupEl.addEventListener("pointerdown", lockBeforeSwitch, true);
      tabGroupEl.addEventListener("keydown", lockBeforeSwitch, true);
    }
  }

  // Clicks land inside sl-tab's shadow tree, so event.target.closest('sl-tab') fails.
  static _tabFromEvent(event) {
    const path = typeof event.composedPath === "function" ? event.composedPath() : [];
    for (const node of path) {
      if (node instanceof HTMLElement && node.tagName === "SL-TAB") {
        return node;
      }
    }
    return event.target?.closest?.("sl-tab") || null;
  }

  static _getTabBody(group) {
    return group.shadowRoot?.querySelector('[part~="body"]') || null;
  }

  static _activePanelName(group) {
    if (!group) return "";
    return (
      group.querySelector("sl-tab-panel[active]")?.getAttribute("name") ||
      group.querySelector('sl-tab[aria-selected="true"]')?.panel ||
      group
        .querySelector('sl-tab[aria-selected="true"]')
        ?.getAttribute("panel") ||
      ""
    );
  }

  static _lockTabBodyHeight(group) {
    if (!group) return;

    const body = CollectionWithSlider._getTabBody(group);
    const activePanel =
      group.querySelector("sl-tab-panel[active]") ||
      Array.from(group.querySelectorAll("sl-tab-panel")).find(
        (panel) => panel.offsetHeight > 0,
      );

    const height = Math.round(
      body?.getBoundingClientRect().height ||
        activePanel?.getBoundingClientRect().height ||
        0,
    );
    if (height <= 0) return;

    if (body) {
      body.style.minHeight = `${height}px`;
    }
    group.style.setProperty("--tab-body-min-height", `${height}px`);
  }

  static _unlockTabBodyHeight(group) {
    if (!group) return;

    const body = CollectionWithSlider._getTabBody(group);
    if (body) {
      body.style.minHeight = "";
    }
    group.style.removeProperty("--tab-body-min-height");
  }

  async _updateSwiper(event) {
    const tabName = event?.detail?.name || "";
    if (tabName !== this.name) return;

    // One frame is enough for the panel to become measurable after Shoelace shows it.
    await new Promise((resolve) => requestAnimationFrame(resolve));

    if (!this.$swiper) {
      this._init();
    } else {
      this.$swiper.update();
      this._doInit();
    }

    CollectionWithSlider._unlockTabBodyHeight(this._tabGroupEl);
  }
}

customElements.define("collection-with-slider", CollectionWithSlider);
